import { createContext, useContext, useEffect, useState } from "react";

import { ChevronRight } from "lucide-react";
import { ChevronsDownUp } from "lucide-react";
import { ChevronsUpDown } from "lucide-react";

interface DOMNodeInfo {
  id: string;
  tagName: string;
  cssId: string | null;
  classes: string[];
  textContent: string;
  children: DOMNodeInfo[];
  isVariable: boolean;
  variableType: string | null;
}

const MathJaxTreeContext = createContext<{
  collapsed: { [key: string]: boolean };
  onCollapse: (id: string, collapsed: boolean) => void;
  selectedNodes: Set<string>;
  onSelectNode: (id: string) => void;
}>({
  collapsed: {},
  onCollapse: () => {},
  selectedNodes: new Set(),
  onSelectNode: () => {},
});

/**
 * Parse a DOM element into a tree structure
 */
const parseDOMNode = (
  element: Element,
  parentId: string,
  index: number
): DOMNodeInfo => {
  const id = `${parentId}-${index}`;
  const htmlEl = element as HTMLElement;
  const classes = Array.from(element.classList);

  // Compute variableType first from the explicit supported classes
  let variableType: string | null = null;
  if (classes.includes("formula-var-input")) variableType = "input";
  else if (classes.includes("formula-var-base")) variableType = "base";

  // Derive isVariable from variableType to ensure consistency
  const isVariable = variableType !== null;

  // Get direct text content (not from children)
  let textContent = "";
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      textContent += node.textContent || "";
    }
  });

  // Parse children
  const children: DOMNodeInfo[] = [];
  Array.from(element.children).forEach((child, childIndex) => {
    children.push(parseDOMNode(child, id, childIndex));
  });

  return {
    id,
    tagName: element.tagName.toLowerCase(),
    cssId: htmlEl.id || null,
    classes,
    textContent: textContent.trim(),
    children,
    isVariable,
    variableType,
  };
};

/**
 * Build tree structure from all formula nodes in the DOM
 */
const buildMathJaxTree = (): DOMNodeInfo[] => {
  const trees: DOMNodeInfo[] = [];
  // Find all formula nodes
  const formulaNodes = document.querySelectorAll(".formula-node");
  formulaNodes.forEach((formulaNode, formulaIndex) => {
    const renderedLatex = formulaNode.querySelector(".rendered-latex");
    if (renderedLatex) {
      // Parse from the MathJax container (usually mjx-container)
      const mjxContainer = renderedLatex.querySelector("mjx-container");
      if (mjxContainer) {
        trees.push(parseDOMNode(mjxContainer, `formula-${formulaIndex}`, 0));
      } else {
        // Fallback to rendered-latex content
        trees.push(parseDOMNode(renderedLatex, `formula-${formulaIndex}`, 0));
      }
    }
  });

  return trees;
};

export const MathJaxTreePane = () => {
  const [trees, setTrees] = useState<DOMNodeInfo[]>([]);
  const [collapsed, setCollapsed] = useState<{ [key: string]: boolean }>({});
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());

  // Build tree on mount and when refresh is needed
  useEffect(() => {
    setTrees(buildMathJaxTree());
  }, []);

  const handleRefresh = () => {
    setTrees(buildMathJaxTree());
  };

  const onCollapse = (id: string, isCollapsed: boolean) => {
    setCollapsed({ ...collapsed, [id]: isCollapsed });
  };

  const onSelectNode = (id: string) => {
    const newSelection = new Set(selectedNodes);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.clear();
      newSelection.add(id);
    }
    setSelectedNodes(newSelection);
  };

  const collapseAll = () => {
    const newCollapsed: { [key: string]: boolean } = {};
    const collapse = (node: DOMNodeInfo) => {
      if (node.children.length > 0) {
        newCollapsed[node.id] = true;
        node.children.forEach(collapse);
      }
    };
    trees.forEach(collapse);
    setCollapsed(newCollapsed);
  };

  return (
    <div className="pt-3 pb-4 gap-4 flex flex-col h-full overflow-hidden select-none text-base">
      <div className="flex flex-row justify-between items-center px-4">
        <div className="flex flex-row gap-2">
          <button
            onClick={handleRefresh}
            className="px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh
          </button>
          <div
            title="Expand all"
            className="flex justify-center items-center cursor-pointer hover:bg-gray-100 rounded-md p-1"
            onClick={() => setCollapsed({})}
          >
            <ChevronsUpDown
              size={16}
              className="text-slate-600 hover:text-slate-900"
            />
          </div>
          <div
            title="Collapse all"
            className="flex justify-center items-center cursor-pointer hover:bg-gray-100 rounded-md p-1"
            onClick={collapseAll}
          >
            <ChevronsDownUp
              size={16}
              className="text-slate-600 hover:text-slate-900"
            />
          </div>
        </div>
      </div>

      {trees.length === 0 ? (
        <div className="text-gray-500 text-sm px-4">
          No MathJax rendered content found. Click Refresh after formulas load.
        </div>
      ) : (
        <div className="overflow-auto flex-grow font-mono text-sm">
          <MathJaxTreeContext.Provider
            value={{
              collapsed,
              onCollapse,
              selectedNodes,
              onSelectNode,
            }}
          >
            {trees.map((tree, index) => (
              <div key={tree.id} className="mb-4 pl-4">
                <div className="text-xs text-gray-400 mb-1">
                  Formula Node {index}
                </div>
                <DOMTreeNode node={tree} />
              </div>
            ))}
          </MathJaxTreeContext.Provider>
        </div>
      )}
    </div>
  );
};

const DOMTreeNode = ({ node }: { node: DOMNodeInfo }) => {
  const { collapsed, onCollapse, selectedNodes, onSelectNode } =
    useContext(MathJaxTreeContext);

  const isCollapsed = collapsed[node.id];
  const hasChildren = node.children.length > 0;

  // Determine badge color based on variable type
  const getBadgeColor = () => {
    switch (node.variableType) {
      case "input":
        return "bg-green-100 text-green-800 border-green-300";
      case "computed":
        return "bg-purple-100 text-purple-800 border-purple-300";
      case "base":
        return "bg-blue-100 text-blue-800 border-blue-300";
      default:
        return "";
    }
  };

  return (
    <div
      className={`${
        selectedNodes.has(node.id)
          ? "bg-slate-100 rounded-lg"
          : "bg-transparent rounded-lg"
      }`}
    >
      <div className="flex flex-row justify-start items-center hover:bg-slate-100 rounded-lg bg-transparent mb-0.5">
        {/* Collapse toggle */}
        <div
          className={`${
            hasChildren ? "visible" : "invisible"
          } cursor-pointer flex justify-center items-center hover:bg-slate-200 rounded-md p-1 ml-1`}
          onClick={() => onCollapse(node.id, !isCollapsed)}
        >
          <ChevronRight
            size={14}
            className={`${isCollapsed ? "rotate-0" : "rotate-90"} text-slate-600 hover:text-slate-900 transition-transform duration-200`}
          />
        </div>

        {/* Node content */}
        <div
          className="pl-2 p-1 hover:bg-slate-100 rounded-lg flex items-center gap-1 whitespace-nowrap"
          onClick={() => onSelectNode(node.id)}
        >
          {/* Tag name */}
          <span className="text-blue-600">&lt;{node.tagName}</span>

          {/* CSS ID if present */}
          {node.cssId && (
            <span className="text-purple-600 font-semibold">
              id="{node.cssId}"
            </span>
          )}

          {/* Variable type badge */}
          {node.variableType && (
            <span
              className={`px-1.5 py-0.5 text-xs rounded border ${getBadgeColor()}`}
            >
              {node.variableType}
            </span>
          )}

          {/* Classes (abbreviated) */}
          {node.classes.length > 0 && (
            <span className="text-gray-400 text-xs">
              .{node.classes.slice(0, 2).join(".")}
              {node.classes.length > 2 && `+${node.classes.length - 2}`}
            </span>
          )}

          <span className="text-blue-600">&gt;</span>

          {/* Text content preview */}
          {node.textContent && (
            <span className="text-gray-600 text-xs truncate max-w-[100px]">
              "{node.textContent}"
            </span>
          )}

          {/* Children count */}
          {hasChildren && (
            <span className="text-gray-400 text-xs">
              ({node.children.length})
            </span>
          )}
        </div>
      </div>

      {/* Children */}
      {!isCollapsed && hasChildren && (
        <div className="ml-6 border-l border-gray-200 pl-2">
          {node.children.map((child) => (
            <DOMTreeNode key={child.id} node={child} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MathJaxTreePane;
